import { createQueryKeys } from '@lukemorales/query-key-factory'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'

import type { MutationHook, QueryHook } from '@/lib/react-query'

import type { CueSerialized } from '~shared/rundown/cue'
import type { Rundown, Runner } from '~shared/types'

import { setCueOrderAtom, rundownAtom } from '../../../store/rundown.store'
import { toastAtom } from '../../../store/toast.store'
import type {
  UploadRundownFileDto,
  AddRundownCueDto,
  DuplicateRundownCueDto,
  DuplicateRundownCuesDto,
} from '../model/dto.ts'
import type {
  GroupRundownCuesParams,
  UploadRundownFileParams,
  AddRundownCueParams,
  UpdateRundownCueParams,
  DuplicateRundownCueParams,
  DeleteRundownCueParams,
  UpdateRundownParams,
  CreateRundownRunnerParams,
  DuplicateRundownCuesParams,
  UngroupRundownCuesParams,
  GetRundownsByTeamParams,
  CloneRundownParams,
} from '../model/params.ts'

import {
  groupRundownCues,
  uploadRundownFile,
  addRundownCue,
  updateRundownCue,
  duplicateRundownCue,
  deleteRundownCue,
  updateRundown,
  createRundownRunner,
  duplicateRundownCues,
  ungroupRundownCues,
  getRundownsByTeam,
  getTemplatesByTeam,
  cloneRundown,
} from './requests.ts'

export const rundownQueryKeys = createQueryKeys('RUNDOWN', {
  byTeamId: (params: GetRundownsByTeamParams) => [params],

  templatesByTeamId: (params: GetRundownsByTeamParams) => [params],
})
export const RUNDOWN_MUTATION_KEYS_PREFIX = 'RUNDOWN'
export const rundownMutationKeys = {
  addCue: [RUNDOWN_MUTATION_KEYS_PREFIX, 'addCue'],
}

export const useRundownsByTeam = ({
  params,
  options,
  hideToast,
}: QueryHook<Rundown[], GetRundownsByTeamParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.byTeamId(params).queryKey,
    queryFn: async () => {
      const { data } = await getRundownsByTeam(params, { toast: !hideToast })

      return data
    },
  })
}

export const useUpdateRundown = ({
  options,
}: MutationHook<Rundown, UpdateRundownParams> = {}) => {
  const setRundown = useSetAtom(rundownAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundown(params)

      return data
    },
    onSuccess(rundown) {
      setRundown(rundown)
    },
  })
}

export const useUploadRundownFile = ({
  options,
}: MutationHook<UploadRundownFileDto, UploadRundownFileParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await uploadRundownFile(params)

      return data
    },
  })
}

export const useAddRundownCue = ({
  options,
}: MutationHook<AddRundownCueDto, AddRundownCueParams> = {}) => {
  // The cue documents themselves are NOT written back, only the order is applied
  // eagerly, because it lives on the rundown and drives where the new row appears.
  const setCueOrder = useSetAtom(setCueOrderAtom)

  return useMutation({
    ...options,
    mutationKey: rundownMutationKeys.addCue,
    mutationFn: async (params) => {
      const { data } = await addRundownCue(params)

      return data
    },
    onSuccess([rundown]) {
      setCueOrder(rundown.cues)
    },
  })
}

export const useUpdateRundownCue = ({
  options,
}: MutationHook<CueSerialized, UpdateRundownCueParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundownCue(params)

      return data
    },
  })
}

export const useDuplicateRundownCue = ({
  options,
}: MutationHook<DuplicateRundownCueDto, DuplicateRundownCueParams> = {}) => {
  // Order only, cue documents come from the listener. See `useAddRundownCue`.
  const setCueOrder = useSetAtom(setCueOrderAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await duplicateRundownCue(params)

      return data
    },
    onSuccess({ rundown }) {
      setCueOrder(rundown.cues)
    },
  })
}

export const useDuplicateRundownCues = ({
  options,
}: MutationHook<DuplicateRundownCuesDto, DuplicateRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await duplicateRundownCues(params)

      return data
    },
  })
}

export const useDeleteRundownCue = ({
  options,
}: MutationHook<Runner, DeleteRundownCueParams> = {}) => {
  const addToast = useSetAtom(toastAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await deleteRundownCue(params)

      return data
    },
    onSuccess() {
      addToast({ title: `Cue moved to trash`, type: 'success', body: '' })
    },
  })
}

export const useCreateRundownRunner = ({
  options,
}: MutationHook<Rundown, CreateRundownRunnerParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await createRundownRunner(params)

      return data
    },
  })
}

export const useGroupRundownCues = ({
  options,
}: MutationHook<Rundown, GroupRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await groupRundownCues(params)

      return data
    },
  })
}

export const useUngroupRundownCues = ({
  options,
}: MutationHook<Rundown, UngroupRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await ungroupRundownCues(params)

      return data
    },
  })
}

export const useTemplatesByTeam = ({
  params,
  options,
}: QueryHook<Rundown[], GetRundownsByTeamParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.templatesByTeamId(params).queryKey,
    queryFn: async () => {
      const { data } = await getTemplatesByTeam(params)

      return data
    },
  })
}

export const useCloneRundown = ({
  options,
}: MutationHook<Rundown, CloneRundownParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await cloneRundown(params)

      return data
    },
  })
}
